import React, { useEffect, Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { showToast } from "../../../components/ToastifyNotification";
import Breadcrumb from "../../../components/common/breadcrumb";

// Excel + PDF
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { DoctorWisePatientStatusReportApi } from "../../../api/ipd/ipdReportsAPI";

const DoctorWisePatientStatusReport = () => {

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [summary, setSummary] = useState([]);      // NEW ← summary matrix

    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [search, setSearch] = useState("");
    const [patientStatus, setPatientStatus] = useState("");

    const dispatch = useDispatch();

    // Patient status master
    const patientStatusList = [
        "Improved",
        "Recovered",
        "Birth",
        "No Changed",
        "Referred",
        "Expired",
        "LAMA",
        "Self Discharge",
    ];

    // Fetch Data
    const fetchData = async () => {
        dispatch({ type: "LOADER_START" });

        try {
            const req = {
                fromDate,
                toDate,
                patientStatus,
            };

            const response = await DoctorWisePatientStatusReportApi(req);

            if (response.success) {

                showToast("success", response.message);

                // Format main data
                const formatted = response.data.map((item, index) => ({
                    index: index + 1,
                    doctorName: item.ipdEntryId?.consultantDoctorId?.name || "Unknown",
                    patientStatus: item.patientStatus || "",
                    billNo: item?.billNo || "",
                    ipdNo: item?.ipdEntryId?.ipdNo || "",
                    dischargeDate: item?.dischargeDate?.split("T")[0] || "",
                    patientName: item?.opdEntryId?.patientName || "",
                }));

                setData(formatted);
                setFilteredData(formatted);

                // ======================
                // Build Summary Matrix
                // ======================
                const summaryData = {};

                formatted.forEach(row => {
                    const doctor = row.doctorName;
                    const status = row.patientStatus;

                    if (!summaryData[doctor]) {
                        summaryData[doctor] = {};
                        patientStatusList.forEach(s => summaryData[doctor][s] = 0);
                    }
                    if (summaryData[doctor][status] !== undefined) {
                        summaryData[doctor][status] += 1;
                    }
                });

                const summaryRows = Object.keys(summaryData).map(doctor => ({
                    doctor,
                    ...summaryData[doctor]
                }));

                setSummary(summaryRows);

            } else {
                showToast("error", response.message);
            }

        } catch (err) {
            showToast("error", err.message);
        } finally {
            dispatch({ type: "LOADER_STOP" });
        }
    };

    // Search filter
    useEffect(() => {
        let result = data;

        if (search) {
            result = result.filter(
                (item) =>
                    item.doctorName.toLowerCase().includes(search.toLowerCase()) ||
                    item.patientName.toLowerCase().includes(search.toLowerCase()) ||
                    item.ipdNo.toLowerCase().includes(search.toLowerCase())
            );
        }

        if (patientStatus) {
            result = result.filter((item) => item.patientStatus === patientStatus);
        }

        setFilteredData(result);
    }, [search, patientStatus, data]);

    // Columns for summary table
    const summaryColumns = [
        { name: "Doctor Name", selector: (row) => row.doctor, sortable: true },
        ...patientStatusList.map(status => ({
            name: status,
            selector: row => row[status],
            sortable: true,
        })),
    ];

    // Export Excel
    const exportToExcel = () => {
        const sheetData = summary.map((row) => row);
        const worksheet = XLSX.utils.json_to_sheet(sheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");
        XLSX.writeFile(workbook, "DoctorWise_Patient_Status_Report.xlsx");
    };

    // Export PDF
    const exportToPDF = () => {
        const doc = new jsPDF();
        doc.text("Doctor Wise Status Report", 14, 10);
        autoTable(doc, {
            head: [summaryColumns.map(c => c.name)],
            body: summary.map(row => summaryColumns.map(c => row[c.selector])),
            startY: 20,
        });
        doc.save("DoctorWise_Patient_Status_Report.pdf");
    };

    return (
        <Fragment>
            <Breadcrumb parent="Doctor Wise Patient Status Report" title="Doctor Wise Patient Status Report" />

            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card">
                            <div className="card-header">
                                <h5>Doctor Wise Patient Status Report</h5>
                                <div className="row mt-3">
                                    <div className="col-sm-2">
                                        <label>From Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={fromDate}
                                            onChange={(e) => setFromDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm-2">
                                        <label>To Date</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={toDate}
                                            onChange={(e) => setToDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="col-sm mt-4">
                                        <button className="btn btn-primary me-2" onClick={fetchData}>Search</button>
                                    </div>
                                    <div className="col-sm text-end mt-4">
                                        <button className="btn btn-success me-2" onClick={exportToExcel}>Excel</button>
                                        <button className="btn btn-danger" onClick={exportToPDF}>PDF</button>
                                    </div>

                                </div>
                            </div>


                            <div className="card-body">
                                <DataTable
                                    columns={summaryColumns}
                                    data={summary}
                                    pagination
                                    dense
                                    highlightOnHover
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </Fragment>
    );
};

export default DoctorWisePatientStatusReport;
